/**
 * ClaimOfferPage (Jose 2026-08-06) — the client's "pick your time" page.
 *
 * Opened from the texted claim link (/claim/:token). Shows the shop's hand-picked slots with a
 * live 5-minute countdown; the client taps one to claim it (first-come-first-served). If the timer
 * runs out or the slot was just taken, it says so cleanly. Self-contained styling so it looks the
 * same wherever it's hosted.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { waitlistOfferService } from '../services/waitlistOfferService';
import {
  WaitlistOffer,
  isOfferActive,
  secondsRemaining,
  formatCountdown,
  offerDayLabel,
  offerTimeLabel,
} from '../shared/waitlistOffer';

type Loaded =
  | { kind: 'loading' }
  | { kind: 'notfound' }
  | { kind: 'ready'; offer: WaitlistOffer };

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, background: 'linear-gradient(160deg,#0f172a 0%,#1e1b4b 60%,#312e81 100%)',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, background: '#fff', borderRadius: 24,
  boxShadow: '0 24px 60px rgba(0,0,0,.4)', overflow: 'hidden',
};

const ClaimOfferPage: React.FC = () => {
  const { token = '' } = useParams();
  const [state, setState] = useState<Loaded>({ kind: 'loading' });
  const [now, setNow] = useState<number>(Date.now());
  const [claiming, setClaiming] = useState<number | null>(null);
  const [claimed, setClaimed] = useState<{ day: string; time: string; barber: string } | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let alive = true;
    waitlistOfferService.getOfferByToken(token).then((offer) => {
      if (!alive) return;
      setState(offer ? { kind: 'ready', offer } : { kind: 'notfound' });
    }).catch(() => alive && setState({ kind: 'notfound' }));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const offer = state.kind === 'ready' ? state.offer : null;
  const active = offer ? isOfferActive(offer, now) : false;
  const secs = offer ? secondsRemaining(offer, now) : 0;
  const low = secs <= 60;

  const claim = async (index: number) => {
    if (!offer || claiming !== null) return;
    setClaiming(index); setError('');
    try {
      const res = await waitlistOfferService.claimOffer(offer, index);
      const [h, m] = res.time.split(':').map(Number);
      setClaimed({ day: offerDayLabel(res.date), time: offerTimeLabel(`${h}:${String(m).padStart(2, '0')}`), barber: res.barberName.split(' ')[0] });
    } catch (e: any) {
      setError(e?.message || 'Could not claim that slot. Please pick another.');
      // Refresh the offer in case its status changed.
      const fresh = await waitlistOfferService.getOfferByToken(token).catch(() => null);
      if (fresh) setState({ kind: 'ready', offer: fresh });
    } finally {
      setClaiming(null);
    }
  };

  const body = useMemo(() => {
    if (state.kind === 'loading') return <Centered>Loading your options…</Centered>;
    if (state.kind === 'notfound') return <Centered>This offer link isn’t valid.</Centered>;

    if (claimed) {
      return (
        <div style={{ padding: '40px 28px', textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>✅</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>You’re booked!</h1>
          <p style={{ color: '#475569', fontSize: 16, margin: 0 }}>
            {claimed.day} at <strong>{claimed.time}</strong> with {claimed.barber}.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 18 }}>See you at SLIK. 💈</p>
        </div>
      );
    }

    const o = state.offer;
    if (o.status === 'claimed') return <Centered>This offer was already claimed. 🎉</Centered>;
    if (o.status === 'cancelled') return <Centered>This offer was cancelled by the shop.</Centered>;
    if (!active) {
      return (
        <div style={{ padding: '40px 28px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⏰</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>This offer expired</h1>
          <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>No worries — reply to the shop and we’ll find you another time.</p>
        </div>
      );
    }

    return (
      <div>
        {/* Countdown banner */}
        <div style={{ background: low ? '#dc2626' : '#4f46e5', color: '#fff', padding: '18px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, opacity: .85, letterSpacing: .3 }}>Hi {o.clientName.split(' ')[0]} — a spot opened up. Claim it before the timer runs out:</div>
          <div style={{ fontSize: 40, fontWeight: 800, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>{formatCountdown(secs)}</div>
          <div style={{ fontSize: 12, opacity: .8 }}>first come, first served</div>
        </div>

        <div style={{ padding: '20px 20px 26px' }}>
          {error && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {o.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => claim(i)}
                disabled={claiming !== null}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: '16px 18px', borderRadius: 16, border: '2px solid #c7d2fe',
                  background: claiming === i ? '#4f46e5' : '#eef2ff', color: claiming === i ? '#fff' : '#312e81',
                  fontSize: 16, fontWeight: 700, cursor: claiming !== null ? 'default' : 'pointer',
                  opacity: claiming !== null && claiming !== i ? .5 : 1, transition: 'all .15s', textAlign: 'left',
                }}
              >
                <span>
                  <span style={{ display: 'block', fontSize: 17 }}>{offerDayLabel(opt.date)}</span>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 500, opacity: .8 }}>with {opt.barberName.split(' ')[0]}</span>
                </span>
                <span style={{ fontSize: 20, fontWeight: 800 }}>{claiming === i ? '…' : offerTimeLabel(opt.time)}</span>
              </button>
            ))}
          </div>
          <p style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', marginTop: 16 }}>Tap a time to lock it in.</p>
        </div>
      </div>
    );
  }, [state, claimed, active, secs, low, error, claiming]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ padding: '16px 24px 0', textAlign: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: '#6366f1' }}>SLIK BARBER LOUNGE</span>
        </div>
        {body}
      </div>
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '48px 28px', textAlign: 'center', color: '#475569', fontSize: 16 }}>{children}</div>
);

export default ClaimOfferPage;
